import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import Wrapper from '../../components/Wrapper';
import { Paper, Typography, Box, Divider, Button, IconButton, Alert } from '@mui/material';
import { shortFunction, file, rate, input } from '../../translate';
import Api from "../../Api";
import { useNavigate } from 'react-router-dom';
import FileUploader from '../../components/new_component/FileUploader';
import SipDragDropZones from '../../components/new_component/SipDragDropZones';
import { toast } from 'react-toastify';
import { REACT_APP_API_URL } from "../../config";
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import InfoIcon from '@mui/icons-material/Info';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

const { REACT_APP_SERVER } = process.env;
const color = REACT_APP_SERVER === 'ATS' ? '#0b73b9' : '#7ebc0a';
const hoverColor = REACT_APP_SERVER === 'ATS' ? '#095a8f' : '#6aa507';

export default function Rate() {
    const langIndex = +localStorage.getItem('atsLang') || 1;
    const [sips, setSips] = useState([]);
    const [loading, setLoading] = useState(false);
    const [beforeFile, setBeforeFile] = useState(null);
    const [afterFile, setAfterFile] = useState(null);
    const navigate = useNavigate()
    const [fileTarget, setFileTarget] = useState('before');
    const [fileModalOpen, setFileModalOpen] = useState(false);
    const [fileModalLoading, setFileModalLoading] = useState(false);
    const [selectedSipsRate, setSelectedSipsRate] = useState([]);
    const [files, setFiles] = useState([]);
    const [saving, setSaving] = useState(false);
    const audioRef = useRef(null);
    const playingIdRef = useRef(null);
    const [playingId, setPlayingId] = useState(null);
    const [helpAnchor, setHelpAnchor] = useState(null);
    const helpCloseTimeoutRef = useRef(null);
    const handleHelpOpen = (e) => {
        if (helpCloseTimeoutRef.current) clearTimeout(helpCloseTimeoutRef.current);
        setHelpAnchor(e.currentTarget);
    };
    const handleHelpClose = () => {
        helpCloseTimeoutRef.current = setTimeout(() => setHelpAnchor(null), 150);
    };
    const handleHelpPopperEnter = () => {
        if (helpCloseTimeoutRef.current) clearTimeout(helpCloseTimeoutRef.current);
    };
    const handleHelpPopperLeave = () => {
        helpCloseTimeoutRef.current = setTimeout(() => setHelpAnchor(null), 150);
    };
    const keySuffix = '&key=akNwNXN1U2k4bzdEQ09QU29KMUpYbXFiQTV5c3FmUm1MOUNheW5qWlYwOD0=';
    const getAudioSrc = (f) => {
        const fid = f?.fname || f?.id || f?.name;
        return fid ? `${REACT_APP_API_URL}/rest/files/audio?file=${fid}${keySuffix}` : null;
    };
    const stopAudio = () => {
        if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current = null;
        }
        setPlayingId(null);
        playingIdRef.current = null;
    };
    useEffect(() => {
        (async () => {
            try {
                const { data } = await Api.fileGet(true);
                setFiles(data?.file || []);
            } catch (e) {
                console.log(e);
            }
        })();
    }, []);
    useEffect(() => {
        if (!beforeFile) {
            const foundBefore = files.find(f => Number(f.rate) === 1);
            if (foundBefore) {
                setBeforeFile(foundBefore);
            }
        }
        if (!afterFile) {
            const foundAfter = files.find(f => Number(f.rate) === 2);
            if (foundAfter) {
                setAfterFile(foundAfter);
            }
        }
    }, [files, beforeFile, afterFile]);
    const handleFileSave = async (file, targetOverride = null) => {
        try {
            setFileModalLoading(true);
            const target = targetOverride || fileTarget;
            const selected = file || (target === 'before' ? beforeFile : afterFile);
            let fid = selected?.id || selected?.fname;
            if (!fid && selected && selected.size) {
                const payload = {
                    file: selected,
                    name: selected.name || 'file',
                    size: selected.size || 0,
                };
                const { data: createData } = await Api.fileCreate(payload);
                const created = createData?.file?.[createData.file.length - 1];
                if (created) {
                    fid = created.id || created.fname;
                    if (target === 'before') {
                        setBeforeFile(created);
                    } else {
                        setAfterFile(created);
                    }
                }
            }
            if (!fid) {
                setFileModalOpen(false);
                return;
            }
            const rateVal = target === 'before' ? 1 : 2;
            const type = target === 'before' ? 'rate' : 'after-rating';
            await Api.fileEdit(fid, rateVal, type);
            const { data } = await Api.fileGet(true);
            setFiles(data?.file || []);
            toast.success(shortFunction.operator_save?.[langIndex] || 'Saved');
            setFileModalOpen(false);
        } catch (e) {
            console.log(e);
        } finally {
            setFileModalLoading(false);
        }
    };
    const handleGlobalSave = async () => {
        try {
            setSaving(true);
            const selectedSipIds = Array.from(new Set(selectedSipsRate)).filter(Boolean);
            await Api.shortSipRate(selectedSipIds);
            const { data: sipData } = await Api.shortSipList();
            setSips(sipData?.data || []);
            toast.success(shortFunction.operator_save?.[langIndex] || 'Saved');
            navigate('/short_function/rate');
        } catch (e) {
            console.log(e);
        } finally {
            setSaving(false);
        }
    };
    const fetchSips = useCallback(async () => {
        try {
            setLoading(true);
            const { data } = await Api.shortSipList();
            const list = data?.data || [];
            setSips(list);
            const preset = list.filter(item => Number(item.rate) === 1).map(item => item.buddy || item.id);
            setSelectedSipsRate(preset);
        } catch (e) {
            setSips([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchSips();
    }, [fetchSips]);

    const beforeLabel = useMemo(() => shortFunction.rate_before?.[langIndex] || 'Before rating file', [langIndex]);
    const afterLabel = useMemo(() => shortFunction.rate_after?.[langIndex] || 'After rating file', [langIndex]);

    return (
        <Wrapper>
            <div className="tableParent">
                <Paper sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, borderBottom: '2px solid rgba(0, 0, 0, 0.08)', pb: 2.5, }}>
                        <IconButton
                            size="small"
                            sx={{ color, fontSize: 18, '&:hover': { backgroundColor: `${color}15` } }}
                            onClick={() => navigate(-1)}
                            aria-label="Back"
                        >
                            <ArrowBackIosNewIcon sx={{ fontSize: 20, fontWeight: 700 }} />
                        </IconButton>
                        <Typography variant="h5" sx={{ fontWeight: 700, m: 0 }}>
                            {shortFunction.rate[langIndex]}
                        </Typography>
                        <IconButton
                            size="small"
                            onMouseEnter={handleHelpOpen}
                            onMouseLeave={handleHelpClose}
                            aria-label={shortFunction.operator_help_tooltip?.[langIndex] || 'How it works'}
                            sx={{ color, '&:hover': { backgroundColor: `${color}15` } }}
                        >
                            <HelpOutlineIcon fontSize="small" />
                        </IconButton>
                    </Box>

                    <Alert
                        icon={<InfoIcon />}
                        severity="success"
                        sx={
                            REACT_APP_SERVER !== 'ATS'
                                ? { mb: 2 }
                                : {
                                    mb: 2,
                                    border: `1px solid ${color}`,
                                    backgroundColor: `${color}10`,
                                    '& .MuiAlert-icon': { color },
                                }
                        }
                    >
                        <Typography variant="body2">
                            {shortFunction.rate_desc?.[langIndex] ||
                                'The Employee Rating function lets callers rate the service quality after a call ends.'}
                        </Typography>
                    </Alert>

                    <Divider />

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Paper elevation={0} sx={{ border: '1px solid #e5e7eb', borderRadius: 2, p: 2, minHeight: 160 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                                {beforeLabel}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                                <Button
                                    variant="outlined"
                                    component="label"
                                    sx={{ textTransform: 'none', color, borderColor: color, '&:hover': { borderColor: hoverColor, color: hoverColor } }}
                                >
                                    {beforeFile ? beforeFile.name : (rate.choose_comp?.[langIndex] || 'Choose from computer')}
                                    <input
                                        type="file"
                                        hidden
                                        accept=".wav,.mp3,audio/wav,audio/mpeg"
                                        onChange={async (ev) => {
                                            const file = ev.target.files?.[0] || null;
                                            if (file) {
                                                const allowed = ['audio/wav', 'audio/x-wav', 'audio/mpeg', 'audio/mp3', 'audio/wave', 'audio/vnd.wave'];
                                                const maxSize = 5 * 1024 * 1024;
                                                if (!allowed.includes(file.type) || file.size > maxSize) {
                                                    toast.error(rate.file_limit?.[langIndex] || 'Only mp3/wav files up to 5MB are allowed.');
                                                    ev.target.value = '';
                                                    return;
                                                }
                                            }
                                            setBeforeFile(file);
                                            if (file) {
                                                await handleFileSave(file, 'before');
                                            }
                                        }}
                                    />
                                </Button>
                                <Button
                                    variant="outlined"
                                    sx={{ textTransform: 'none', color, borderColor: color, '&:hover': { borderColor: hoverColor, color: hoverColor } }}
                                    onClick={() => { setFileTarget('before'); setFileModalOpen(true); }}
                                >
                                    {rate.choose_file?.[langIndex] || 'Choose from existing files'}
                                </Button>
                            </Box>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                {rate.file_limit?.[langIndex] || 'Only mp3/wav files up to 5MB are allowed.'}
                            </Typography>
                            {beforeFile && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    {beforeFile ? (
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            sx={{ minWidth: 36, padding: '2px 8px', color, borderColor: color, '&:hover': { borderColor: hoverColor, color: hoverColor } }}
                                            onClick={async () => {
                                                const fid = beforeFile.fname || beforeFile.id || beforeFile.name;
                                                if (!fid) return;
                                                if (playingId === fid) {
                                                    stopAudio();
                                                    return;
                                                }
                                                try {
                                                    stopAudio();
                                                    const src = getAudioSrc(beforeFile);
                                                    if (!src) return;
                                                    const audio = new Audio(src);
                                                    audioRef.current = audio;
                                                    playingIdRef.current = fid;
                                                    setPlayingId(fid);
                                                    audio.onended = () => {
                                                        stopAudio();
                                                    };
                                                    audio.onerror = () => {
                                                        stopAudio();
                                                    };
                                                    await audio.play();
                                                } catch (e) {
                                                    console.log(e);   
                                                    stopAudio();
                                                }
                                            }}
                                        >
                                            {playingId === (beforeFile.fname || beforeFile.id || beforeFile.name) ? '■' : '▶'}
                                        </Button>
                                    ) : null}
                                    <Typography variant="body2" color="text.secondary">{beforeFile.name}</Typography>
                                    
                                </Box>
                            )}
                        </Paper>

                        <Paper elevation={0} sx={{ border: '1px solid #e5e7eb', borderRadius: 2, p: 2, minHeight: 160 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                                {afterLabel}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                                <Button
                                    variant="outlined"
                                    component="label"
                                    sx={{ textTransform: 'none', color, borderColor: color, '&:hover': { borderColor: hoverColor, color: hoverColor } }}
                                >
                                    {afterFile ? afterFile.name : (rate.choose_comp?.[langIndex] || 'Choose from computer')}
                                    <input
                                        type="file"
                                        hidden
                                        accept=".wav,.mp3,audio/wav,audio/mpeg"
                                        onChange={async (ev) => {
                                            const file = ev.target.files?.[0] || null;
                                            if (file) {
                                                const allowed = ['audio/wav', 'audio/x-wav', 'audio/mpeg', 'audio/mp3', 'audio/wave', 'audio/vnd.wave'];
                                                const maxSize = 5 * 1024 * 1024;
                                                if (!allowed.includes(file.type) || file.size > maxSize) {
                                                    toast.error(rate.file_limit?.[langIndex] || 'Only mp3/wav files up to 5MB are allowed.');
                                                    ev.target.value = '';
                                                    return;
                                                }
                                            }
                                            setAfterFile(file);
                                            if (file) {
                                                await handleFileSave(file, 'after');
                                            }
                                        }}
                                    />
                                </Button>
                                <Button
                                    variant="outlined"
                                    sx={{ textTransform: 'none', color, borderColor: color, '&:hover': { borderColor: hoverColor, color: hoverColor } }}
                                    onClick={() => { setFileTarget('after'); setFileModalOpen(true); }}
                                >
                                    {rate.choose_file?.[langIndex] || 'Choose from existing files'}
                                </Button>
                            </Box>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                {rate.file_limit?.[langIndex] || 'Only mp3/wav files up to 5MB are allowed.'}
                            </Typography>
                            {afterFile && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    {afterFile ? (
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            sx={{ minWidth: 36, padding: '2px 8px', color, borderColor: color, '&:hover': { borderColor: hoverColor, color: hoverColor } }}
                                            onClick={async () => {
                                                const fid = afterFile.fname || afterFile.id || afterFile.name;
                                                if (!fid) return;
                                                if (playingId === fid) {
                                                    stopAudio();
                                                    return;
                                                }
                                                try {
                                                    stopAudio();
                                                    const src = getAudioSrc(afterFile);
                                                    if (!src) return;
                                                    const audio = new Audio(src);
                                                    audioRef.current = audio;
                                                    playingIdRef.current = fid;
                                                    setPlayingId(fid);
                                                    audio.onended = () => {
                                                        stopAudio();
                                                    };
                                                    audio.onerror = () => {
                                                        stopAudio();
                                                    };
                                                    await audio.play();
                                                } catch (e) {
                                                    console.log(e);
                                                    stopAudio();
                                                }
                                            }}
                                        >
                                            {playingId === (afterFile.fname || afterFile.id || afterFile.name) ? '■' : '▶'}
                                        </Button>
                                    ) : null}
                                    <Typography variant="body2" color="text.secondary">{afterFile.name}</Typography>
                                    
                                </Box>
                            ) }
                        </Paper>
                    </Box>

                    <Divider />


                    <SipDragDropZones
                        items={sips}
                        selectedIds={selectedSipsRate}
                        onSelectionChange={setSelectedSipsRate}
                        zoneNotSelectedTitle={shortFunction.rate_not_selected?.[langIndex] || 'Not using rating'}
                        zoneSelectedTitle={shortFunction.rate_selected?.[langIndex] || 'Using rating'}
                        zoneNotSelectedId="rate-zone-not-selected"
                        zoneSelectedId="rate-zone-selected"
                        getItemId={(item) => item.buddy || item.id}
                        loading={loading}
                        emptyMessage={shortFunction.operator_empty?.[langIndex] || 'No data'}
                        helpAnchorEl={helpAnchor}
                        onHelpPopperEnter={handleHelpPopperEnter}
                        onHelpPopperLeave={handleHelpPopperLeave}
                        helpTitle={shortFunction.operator_help_title?.[langIndex] || 'How to use'}
                        helpDescription={shortFunction.rate_desc?.[langIndex] || shortFunction.operator_help_desc?.[langIndex] || 'Drag employees to the right to enable rating after the call.'}
                        helpDragMeText={shortFunction.operator_help_drag_me?.[langIndex] || 'SIP — drag me'}
                        searchPlaceholder={input.search[langIndex]}
                    />
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                        <Button
                            variant="outlined"
                            sx={{ textTransform: 'none', color, borderColor: color, '&:hover': { borderColor: hoverColor, color: hoverColor } }}
                            onClick={() => { 
                                navigate('/short_function');
                                setBeforeFile(null); setAfterFile(null); }}
                        >
                            {shortFunction.operator_close?.[langIndex] || 'Cancel'}
                        </Button>
                        <Button
                            variant="contained"
                            sx={{ textTransform: 'none', backgroundColor: color, color: '#fff', '&:hover': { backgroundColor: hoverColor } }}
                            disabled={saving}
                            onClick={handleGlobalSave}
                        >
                            {saving ? (shortFunction.operator_loading?.[langIndex] || 'Saving...') : (shortFunction.operator_save?.[langIndex] || 'Save')}
                        </Button>
                    </Box>
                </Paper>
                <FileUploader
                    open={fileModalOpen}
                    onClose={() => setFileModalOpen(false)}
                    files={files}
                    loading={fileModalLoading}
                    target={fileTarget}
                    selectedId={
                        fileTarget === 'before'
                            ? (beforeFile?.id || beforeFile?.fname || beforeFile?.name)
                            : (afterFile?.id || afterFile?.fname || afterFile?.name)
                    }
                    onUpload={(f) => {
                        if (fileTarget === 'before') {
                            setBeforeFile(f);
                        } else {
                            setAfterFile(f);
                        }
                    }}
                    onSelect={(f) => {
                        if (fileTarget === 'before') {
                            setBeforeFile(f);
                        } else {
                            setAfterFile(f);
                        }
                    }}
                    onSave={handleFileSave}
                />
            </div>
        </Wrapper>
    )
}
